import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import RewardCardsEffect from '../../../Prototype/Effect/RewardCardsEffect';
import Point from '../../../Utility/Point';
import { delay } from '../../../Utility/Task/TaskQueue';
import Sprite from '../../../Prototype/Sprite';
import TransformComponent from '../../../Component/Transform';
import { Easing, Tween } from '@tweenjs/tween.js';
import ColorFilter from '../../../Component/ColorFilter';
import RewardCardsNodeEffect from '../../../Prototype/Effect/RewardCardsNodeEffect';
import ScoreCardEffect from '../../../Prototype/Effect/ScoreCardEffect';
import Container from '../../../Prototype/Container';
import Game from '../../../Game';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';

interface IRewardCardsData {
    cards: 1 | 2 | 3;
    node: GameObject;
}

export class RewardCards extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: IRewardCardsData
    ): IterableIterator<number> {
        const backCardTexture = Game.state.getState().playerState.backcard;
        yield* this.loadBackCardtexture(backCardTexture);

        animator.scene.emit(RenderSystemEvent.BRING_TO_FRONT, animator);

        const layoutContainer: GameObject = GameObject.Instantiate(Container, {
            name: 'layoutContainer',
            relativePosition: new Point(0.5, 0.5),
        });
        animator.addChild(layoutContainer);

        // Show currency reward effect and destroy it after complete.
        const rewardEffect: GameObject = GameObject.Instantiate(
            RewardCardsEffect
        );
        layoutContainer.addChild(rewardEffect);
        rewardEffect.on(DragonbonesAnimationEvent.COMPLETE, () => {
            rewardEffect.destroy();
        });

        // Positions for 1, 2 or 3 cards.
        const newCardsPositions: any[] = [
            [{ x: 0, y: 0, rotation: 0 }],
            [
                { x: -125, y: 25, rotation: -Math.PI * 0.15 },
                { x: 125, y: 25, rotation: Math.PI * 0.15 },
            ],
            [
                { x: -125, y: 50, rotation: -Math.PI * 0.15 },
                { x: 0, y: -150, rotation: 0 },
                { x: 125, y: 50, rotation: Math.PI * 0.15 },
            ],
        ];

        const duration: number = 500;

        // Create and show card(s) sprite.
        const newCards: GameObject[] = [];
        for (let i = 0; i < meta.cards; i++) {
            const card: GameObject = GameObject.Instantiate(Sprite, {
                anchor: new Point(0.5, 0.5),
                scale: new Point(0.25, 0.25),
                texture: backCardTexture,
            });
            layoutContainer.addChild(card);
            newCards.push(card);

            this.showCard(card, newCardsPositions[meta.cards - 1][i], duration);
        }

        yield* delay(duration);

        // Create an empty container to get the node position.
        const fakeNode: GameObject = GameObject.Instantiate(Container);
        meta.node.addChild(fakeNode);
        layoutContainer.addChild(fakeNode);
        const nodeTransform = fakeNode.getComponent(TransformComponent);

        // Mode card(s) to the node position.
        for (let card of newCards) {
            yield* this.moveCardToLevelNode(card, nodeTransform, duration);
        }

        yield* delay(100);

        // Show node effect and destroy it after complete.
        const nodeEffect: GameObject = GameObject.Instantiate(
            RewardCardsNodeEffect,
            {
                position: new Point(nodeTransform.x - 5, nodeTransform.y - 10),
            }
        );
        layoutContainer.addChild(nodeEffect);
        nodeEffect.on(DragonbonesAnimationEvent.COMPLETE, () => {
            nodeEffect.destroy();
        });

        yield* delay(300);

        // Pop in from the node the number of cards.

        let text: string;
        if (meta.cards === 1) {
            text = Game.locale.getText('Plus1Card');
        } else {
            text = Game.locale
                .getText('WheelAddCards')
                .replace('[x]', meta.cards.toString());
        }

        layoutContainer.addChild(
            GameObject.Instantiate(ScoreCardEffect, {
                text: text,
                position: nodeTransform.position.clone(),
            })
        );

        yield* delay(1500);

        layoutContainer.destroy();
        fakeNode.destroy();
    }

    private showCard(card: GameObject, target: any, duration: number): void {
        const cardTransform: TransformComponent = card.getComponent(
            TransformComponent
        );
        const cardColorFilter: ColorFilter = card.addComponent(ColorFilter, {
            brightness: 10,
        });

        const tweenPosition: Tween = this.getTween(cardTransform.position);
        const tweenRotation: Tween = this.getTween(cardTransform);
        const tweenScale: Tween = this.getTween(cardTransform.scale);
        const tweenBrightness: Tween = this.getTween(cardColorFilter);

        tweenPosition
            .to({ x: target.x, y: target.y }, duration)
            .easing(Easing.Cubic.Out)
            .start(this.elapsedTime);

        tweenRotation
            .to({ rotation: target.rotation }, duration)
            .easing(Easing.Quadratic.Out)
            .start(this.elapsedTime);

        tweenScale
            .to({ x: 1, y: 1 }, duration)
            .easing(Easing.Cubic.Out)
            .start(this.elapsedTime);

        tweenBrightness
            .to({ brightness: 1 }, duration)
            .easing(Easing.Quadratic.Out)
            .start(this.elapsedTime)
            .onComplete(() => {
                if (card && !card.destroyed) {
                    card.removeComponent(ColorFilter);
                }
            });
    }

    private *moveCardToLevelNode(
        card: GameObject,
        nodeTransform: TransformComponent,
        duration: number
    ) {
        const cardTransform: TransformComponent = card.getComponent(
            TransformComponent
        );

        const tweenPosition: Tween = this.getTween(cardTransform.position);
        const tweenRotation: Tween = this.getTween(cardTransform);
        const tweenScale: Tween = this.getTween(cardTransform.scale);

        const rotationDir = Math.sign(nodeTransform.x - cardTransform.x);

        tweenPosition
            .to({ x: nodeTransform.x, y: nodeTransform.y }, duration)
            .easing(Easing.Cubic.In)
            .start(this.elapsedTime);

        tweenRotation
            .to({ rotation: Math.PI * rotationDir }, duration)
            .easing(Easing.Quadratic.In)
            .start(this.elapsedTime);

        tweenScale
            .to({ x: 0.4, y: 0.4 }, duration)
            .easing(Easing.Cubic.In)
            .onComplete(() => card.destroy())
            .start(this.elapsedTime);

        yield* delay(100);
    }

    private *loadBackCardtexture(
        backCardTexture: string
    ): IterableIterator<number> {
        let textureLoaded = false;

        Game.resource.add(
            backCardTexture,
            `resource/graphics/card_backs/${backCardTexture}.png`
        );

        Game.resource.wait(backCardTexture).then(() => (textureLoaded = true));

        while (!textureLoaded) yield;
    }
}
